import environment from '../config/environment';
import getUrlPath from './getUrlPath';
import logger from './logger';
import { AUTH_REDIRECT_CALLBACK_PATH, AUTH_REDIRECT_STORAGE_KEY } from '../constants/auth';
import { Auth0ContextInterface, getIdentity, User } from '@equipmentshare/auth0-react';
import { AuthRedirectData, AuthRedirectType } from '../types/route';
import { url } from '../constants/url';
import { URL_PATTERN_REGEX } from '../constants/regex';

class Auth {
  private auth0: Auth0ContextInterface<User> | undefined;

  setAuth0(auth0: Auth0ContextInterface<User> | undefined): void {
    this.auth0 = auth0;
  }

  private getLoginRedirectUrl = (
    redirectUrlString?: string | null,
    enforceRedirectUrl?: boolean,
  ) => {
    const currentPath = window.location.pathname;
    const invitePath = getUrlPath(url.signup.subPath.inviteTeam.id);
    const cartPath = getUrlPath(url.rental.subPath.cart.id);
    const checkoutPath = getUrlPath(url.rental.subPath.checkout.id);
    const dashboardPath = getUrlPath(url.account.id);

    const createUrl = (urlString: string): URL => {
      return URL_PATTERN_REGEX.test(urlString)
        ? new URL(urlString)
        : new URL(urlString, window.location.origin);
    };

    const isAllowedPath = (urlString?: string | null): boolean => {
      if (redirectUrlString && enforceRedirectUrl) {
        return true;
      }
      const path = urlString ? createUrl(urlString).pathname : undefined;
      return path
        ? path === invitePath ||
            path === cartPath ||
            path === checkoutPath ||
            (dashboardPath ? path.startsWith(dashboardPath) : false)
        : false;
    };

    const overrideRedirectUrl = (urlString: string | undefined): string => {
      const path = urlString ? createUrl(urlString).pathname : undefined;
      // If user is logging in, send user to cart screen instead of checkout to review their settings
      if (path === checkoutPath) {
        urlString = cartPath;
      }

      if (!urlString) {
        urlString = dashboardPath ?? window.location.origin;
      }

      return urlString;
    };

    let finalRedirectUrl: string | undefined;

    if (redirectUrlString && isAllowedPath(redirectUrlString)) {
      finalRedirectUrl = redirectUrlString;
    } else if (isAllowedPath(currentPath)) {
      finalRedirectUrl = currentPath;
    }

    return overrideRedirectUrl(finalRedirectUrl);
  };

  login = (redirectUrlString?: string | null, enforceRedirectUrl?: boolean) => {
    if (!this.auth0) {
      return;
    }
    try {
      const finalRedirectUrl = this.getLoginRedirectUrl(redirectUrlString, enforceRedirectUrl);

      const redirectData: AuthRedirectData = {
        url: finalRedirectUrl,
        type: AuthRedirectType.LOGIN,
      };
      localStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, JSON.stringify(redirectData));

      const redirectUri = window.location.origin + AUTH_REDIRECT_CALLBACK_PATH;
      this.auth0.loginWithRedirect({
        redirectUri: redirectUri,
        branding: 'equipmentSharePlusT3',
        identifier_type: 'email',
        backToLoginLabel: 'Back to login',
      });
    } catch (error) {
      logger('error', 'at auth:login function', error);
    }
  };

  loginAfterForceLogout(redirectUrlString?: string | null, enforceRedirectUrl?: boolean) {
    if (!this.auth0) {
      return;
    }
    try {
      const finalRedirectUrl = this.getLoginRedirectUrl(redirectUrlString, enforceRedirectUrl);

      if (!this.auth0.isAuthenticated) {
        this.login(redirectUrlString, enforceRedirectUrl);
        return;
      }

      const redirectData: AuthRedirectData = {
        url: finalRedirectUrl,
        type: AuthRedirectType.LOGIN_AFTER_FORCE_LOGOUT,
        enforceRedirectUrl: enforceRedirectUrl ? true : false,
      };

      localStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, JSON.stringify(redirectData));

      const returnTo = window.location.origin + AUTH_REDIRECT_CALLBACK_PATH;
      this.auth0.logout({
        client_id: environment.AUTH0_CONFIG.CLIENT_ID,
        returnTo: returnTo,
      });
    } catch (error) {
      logger('error', 'at auth:loginAfterForceLogout function', error);
    }
  }

  logout = () => {
    if (!this.auth0) {
      return;
    }
    try {
      const redirectData: AuthRedirectData = {
        url: window.location.href,
        type: AuthRedirectType.LOGOUT,
      };
      localStorage.setItem(AUTH_REDIRECT_STORAGE_KEY, JSON.stringify(redirectData));
      this.auth0.logout({
        client_id: environment.AUTH0_CONFIG.CLIENT_ID,
        returnTo: window.location.origin,
      });
    } catch (error) {
      logger('error', 'at auth:logout function', error);
    }
  };

  getAuthIdentity = async () => {
    if (!this.auth0) {
      return;
    }
    return getIdentity(this.auth0);
  };

  getToken = async () => {
    if (!this.auth0) {
      return;
    }
    return this.auth0.getAccessTokenSilently();
  };

  isAutheticated = () => {
    if (!this.auth0) {
      return;
    }
    return this.auth0.isAuthenticated;
  };

  isLoading = () => {
    if (!this.auth0) {
      return;
    }
    return this.auth0.isLoading;
  };
}

export const auth = new Auth();
